import { BinaryWriter, ProtocolError } from "../binary.js";
import { ItemKind, ItemRarity, ItemSlot } from "../items.js";
import { ClientOp, ServerOp } from "../opcodes.js";
/**
 * O auto-loot (PRD §6).
 *
 * ## O problema que ele resolve
 *
 * Uma caçada de uma hora são umas duzentas mortes, e cada morte deixa um corpo com de zero a
 * três pilhas dentro. Sem auto-loot são seiscentos cliques por hora, e o que o jogador faz
 * na prática é **parar de saquear**: ele deixa o ouro no chão porque abrir e recolher custa
 * mais atenção do que a moeda vale. O sistema de loot inteiro vira decoração.
 *
 * A resposta não é dar tudo de graça — é deixar o jogador dizer, uma vez, o que interessa. O
 * que está na lista chega sozinho à mochila; o resto continua dentro do corpo, e revistar
 * continua sendo um gesto que existe.
 *
 * ## Por que a lista mora no servidor
 *
 * É a única coisa desta feature que não pode morar em `localStorage` como as preferências de
 * painel (ver `ui/panel.ts`). Quem recolhe é o servidor, no instante do abate, e uma lista
 * que só o cliente conhece obrigaria o cliente a mandar um pacote de saque por item — que é
 * exatamente o gesto que o auto-loot existe para apagar, agora escrito em rede em vez de em
 * cliques. Além disso ela é do **personagem**: quem entra de outra máquina não quer
 * recomeçar a marcar itens.
 *
 * ## Por que o dinheiro é um interruptor e não uma linha da lista
 *
 * Porque ninguém deixa moeda no chão. Ouro, platina e o que vier depois são a única família
 * que **todo** jogador quer em **toda** caçada, e fazê-la passar pela mesma lista dos outros
 * itens custaria três marcações repetidas por personagem para dizer o que já se sabia. O
 * interruptor também sobrevive a moeda nova entrar no catálogo, e a lista não sobreviveria.
 */
/** Auto-loot ligado. Desligar preserva a lista — é pausa, não apagamento. */
export const LOOT_FILTER_ENABLED = 1 << 0;
/** Recolher toda moeda, sem precisar marcá-la item a item. */
export const LOOT_FILTER_MONEY = 1 << 1;
/**
 * Teto de itens marcados.
 *
 * O catálogo tem centenas de tipos e a lista é um pacote que o **cliente** manda: sem teto,
 * um cliente adulterado descreve uma lista de sessenta mil entradas e o servidor a percorre
 * a cada abate de cada jogador. Sessenta e quatro é largo para quem joga — o loot que
 * interessa numa caçada real são cinco ou seis itens — e estreito para quem não joga.
 */
export const MAX_LOOT_FILTER = 64;
/**
 * As famílias que a lista sabe marcar de uma vez (PRD §6).
 *
 * ## Por que não é `ItemKind`
 *
 * Porque `ItemKind` responde a outra pergunta. **Cinquenta e duas** peças do catálogo são
 * `ItemKind.Armor`, e entre elas estão o elmo, a couraça, as calças, as botas, o amuleto e o
 * anel — a categoria que o jogador tem na cabeça ("quero as calças que caírem") não existe
 * nesse eixo. Quem a carrega é o `ItemSlot`, que é justamente o campo que diz onde a peça
 * veste.
 *
 * Nem só de `ItemSlot`, porém: poção, runa, comida e ferramenta não vestem em lugar nenhum, e
 * para elas o `ItemKind` é a resposta certa. A lista abaixo é a **união** dos dois eixos, cada
 * entrada tirada do que a distingue de verdade — e é por isso que ela é uma tabela própria e
 * não um apelido para nenhum dos dois.
 *
 * ## O que fica de fora, e por quê
 *
 * Moeda tem interruptor próprio. Contêiner e corpo não entram: um deles não se carrega, e o
 * outro chega cheio das coisas de outra pessoa — recolher uma bolsa por engano é o pior
 * resultado possível de uma tela feita para poupar atenção. Chave também não: ela é sempre
 * item de missão, e quem a pega, pega de propósito.
 */
export const LootCategory = {
    Weapon: 0,
    Shield: 1,
    Head: 2,
    Body: 3,
    Legs: 4,
    Feet: 5,
    Amulet: 6,
    Ring: 7,
    Ammo: 8,
    Potion: 9,
    Rune: 10,
    Food: 11,
    Tool: 12,
    /** O que a criatura deixa e ninguém veste: dente, escama, pelo, pena. */
    Valuable: 13,
};
/** Rótulo de cada família, para a tela. Declarado aqui porque a lista é contrato. */
export const LOOT_CATEGORY_NAMES = {
    [LootCategory.Weapon]: 'Armas',
    [LootCategory.Shield]: 'Escudos',
    [LootCategory.Head]: 'Elmos',
    [LootCategory.Body]: 'Armaduras',
    [LootCategory.Legs]: 'Calças',
    [LootCategory.Feet]: 'Botas',
    [LootCategory.Amulet]: 'Amuletos',
    [LootCategory.Ring]: 'Anéis',
    [LootCategory.Ammo]: 'Munição',
    [LootCategory.Potion]: 'Poções',
    [LootCategory.Rune]: 'Runas',
    [LootCategory.Food]: 'Comida',
    [LootCategory.Tool]: 'Ferramentas',
    [LootCategory.Valuable]: 'Materiais',
};
/** Toda categoria, na ordem em que a tela as mostra. */
export const LOOT_CATEGORIES = Object.values(LootCategory);
/**
 * A que família este item pertence, ou `undefined` para o que a lista não marca em bloco.
 *
 * O **slot decide primeiro**, e a ordem é a regra inteira: uma couraça e um amuleto são os
 * dois `ItemKind.Armor`, e perguntar ao `kind` antes jogaria os dois na mesma família — que é
 * exatamente a categoria que não existe para quem joga. Só o que não veste em lugar nenhum
 * chega ao segundo teste.
 */
export function lootCategoryOf(type) {
    switch (type.slot) {
        case ItemSlot.Weapon:
            return LootCategory.Weapon;
        case ItemSlot.Shield:
            return LootCategory.Shield;
        case ItemSlot.Head:
            return LootCategory.Head;
        case ItemSlot.Armor:
            return LootCategory.Body;
        case ItemSlot.Legs:
            return LootCategory.Legs;
        case ItemSlot.Feet:
            return LootCategory.Feet;
        case ItemSlot.Amulet:
            return LootCategory.Amulet;
        case ItemSlot.Ring:
            return LootCategory.Ring;
        case ItemSlot.Ammo:
            return LootCategory.Ammo;
        // `ItemSlot.Backpack` cai fora de propósito: contêiner não entra na lista.
        default:
            break;
    }
    switch (type.kind) {
        case ItemKind.Consumable:
            return LootCategory.Potion;
        case ItemKind.Rune:
            return LootCategory.Rune;
        case ItemKind.Food:
            return LootCategory.Food;
        case ItemKind.Tool:
            return LootCategory.Tool;
        case ItemKind.Generic:
            return LootCategory.Valuable;
        default:
            return undefined;
    }
}
/**
 * "Nenhuma raridade mínima", no campo de um byte.
 *
 * Sentinela e não `Common`, porque `Common` é uma escolha válida e diferente: ela significa
 * "traga tudo o que cair", e desligado significa "não use esta régua". Sem separá-las, a
 * única forma de desligar o filtro de raridade seria ligá-lo no valor mais permissivo — que é
 * o oposto do que o jogador pediu.
 */
export const LOOT_RARITY_OFF = 0xff;
/**
 * `0x14 LootFilter` — cliente→servidor, e `0x96` no sentido inverso.
 *
 * O mesmo corpo nos dois sentidos, e um codificador só para os dois: é o mesmo estado, e a
 * única diferença é quem está contando a quem. Um par de formatos separados seria um par
 * livre para divergir num campo que só existe para ida e volta.
 *
 * As três formas de marcar viajam juntas porque são **uma** escolha: o que entra é o que
 * qualquer uma delas aceita, e mandá-las em pacotes separados abriria a janela em que o
 * servidor conhece a lista de itens nova com as categorias velhas.
 *
 * ```
 * op:u8 | flags:u8 | categories:u16 | minRarity:u8 | count:u8 | (typeId:u16){count}
 * ```
 */
function encode(opcode, packet, writer) {
    // O corte acontece **aqui** e não no decodificador do outro lado: quem escreve uma lista
    // maior que o teto é código nosso com defeito, e cortar em silêncio na saída é preferível
    // a derrubar a conexão de quem só marcou itens demais na tela.
    const typeIds = packet.typeIds.slice(0, MAX_LOOT_FILTER);
    writer
        .u8(opcode)
        .u8(packet.flags)
        .u16(packet.categories)
        .u8(packet.minRarity ?? LOOT_RARITY_OFF)
        .u8(typeIds.length);
    for (const typeId of typeIds)
        writer.u16(typeId);
    return writer;
}
export function encodeLootFilter(packet, writer = new BinaryWriter(8 + MAX_LOOT_FILTER * 2)) {
    return encode(ClientOp.LootFilter, packet, writer);
}
export function encodeLootFilterState(packet, writer = new BinaryWriter(8 + MAX_LOOT_FILTER * 2)) {
    return encode(ServerOp.LootFilter, packet, writer);
}
export function decodeLootFilter(reader) {
    const flags = reader.u8();
    const categories = reader.u16();
    const rarity = reader.u8();
    const count = reader.u8();
    // `count` é `u8`, então 255 é o máximo que cabe no campo — e o teto é 64. Um número entre
    // os dois é contrato quebrado e não corrida de rede: derruba a conexão, como todo frame
    // malformado. Sem esta linha o excesso entraria calado e viveria no banco do personagem.
    if (count > MAX_LOOT_FILTER) {
        throw new ProtocolError(`lista de auto-loot longa demais: ${count}`);
    }
    const typeIds = new Array(count);
    for (let i = 0; i < count; i++)
        typeIds[i] = reader.u16();
    return {
        flags,
        categories,
        // Raridade fora do domínio vira "desligado" em vez de derrubar a conexão, pela mesma
        // disciplina de `decodeStance` com a postura: o campo tem seis valores num byte inteiro
        // de espaço, e um cliente de outra versão mandando o sétimo é a situação normal de um
        // servidor que já atualizou. A sentinela morre aqui, que é onde ela nasceu.
        minRarity: rarity >= ItemRarity.Common && rarity <= ItemRarity.Celestial
            ? rarity
            : null,
        typeIds,
    };
}
//# sourceMappingURL=loot-filter.js.map